import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { memo } from 'react';

import { MILEAGE_FLAG_THRESHOLD } from '@/constants/trips';
import { getEstimatedMileage } from '@/utils';

import { MileageContainer } from './EstimatedMileage.styles';

/**
 * If 'estimated mileage' is not provided calculates the estimated mileage between an origin and a destination.
 * Highlights the mileage if it exceeds the organization-specific threshold.
 *
 * @component
 *
 * @param {Object} props
 * @param {number[]} props.origin - Array containing [longitude, latitude] of the pickup location.
 * @param {number[]} props.destination - Array containing [longitude, latitude] of the dropoff location.
 * @param {number[]} props.estimatedMileage - Number containing the estimated mileage from google.
 * @param {string} props.orgResourceId - Organization ID used to check if mileage exceeds threshold.
 *
 * @returns {React.ReactNode} JSX element displaying formatted mileage or 'N/A' if both 'estimated mileage' and 'coordinates' are missing.
 *
 * @example
 * <EstimatedMileage
 *   origin={[-122.4291581, 37.7857702]}
 *   destination={[-73.9849772, 40.7488551]}
 *   estimatedMileage=8.51
 *   orgResourceId="org123"
 * />
 */

const EstimatedMileage = ({
  origin,
  destination,
  estimatedMileage,
  orgResourceId,
}) => {
  if (_.isNaN(Number(estimatedMileage)) && (!origin || !destination))
    return 'N/A';

  let mileage;

  if (_.isNaN(Number(estimatedMileage))) {
    mileage = getEstimatedMileage(origin, destination);
  } else mileage = estimatedMileage;

  // Show mileage in red if it exceeds the threshold of that org
  const outOfLimit =
    MILEAGE_FLAG_THRESHOLD?.[orgResourceId] &&
    mileage >= MILEAGE_FLAG_THRESHOLD?.[orgResourceId];

  return (
    <MileageContainer $outOfLimit={outOfLimit}>
      {mileage?.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}
    </MileageContainer>
  );
};

EstimatedMileage.propTypes = {
  origin: PropTypes.arrayOf(PropTypes.number).isRequired,
  destination: PropTypes.arrayOf(PropTypes.number).isRequired,
  estimatedMileage: PropTypes.number,
  orgResourceId: PropTypes.string.isRequired,
};

export default memo(EstimatedMileage);
